import { useLazyVacationsQuery, useVacationsQuery } from 'app/services/edpApiV1/vacations';
import { useEffect, useState } from 'react';
import { TableArchive } from 'features/tables/tableArchive';
import { dataForVacationTable, sortingDate } from 'shared/helpers/utils';
import { Badge, Skeleton } from 'antd';

import { RequestsSearch, TColumns } from 'shared/types/types';
import { usePullSort } from 'shared/hooks';
import { SortCondition } from 'shared/enums';
import SuperviserApproveMarker from 'shared/components/superviserApproveMarker';
import { PresetStatusColorType } from 'antd/lib/_util/colors';
import { TPreparedVacationInfoData, TVacationInfo } from 'shared/types/vacations.types';
import { SearchKeysNews } from 'shared/enums/news';
import { useAppSelector } from 'app/store';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { DayOffsStatusTypesMap } from 'shared/types/dayoff.types';
import style from './index.module.scss';

export const VacationArchiveOther = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataVacations, setDataVacations] = useState<TPreparedVacationInfoData[] | []>([]);
  const [sortInfo, sortTrigger] = usePullSort();
  const sortOption = sortInfo.value && sortInfo.value === SortCondition.ASCEND ? SortCondition.ASC : SortCondition.DESC;
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });

  const [getVacations, { data, isLoading }] = useLazyVacationsQuery();

  useEffect(() => {
    getVacations({
      type: 'otherArchive',
      size: currentSize,
      page: currentPage,
      order: sortInfo.value ? sortOption : undefined,
      allAvailableOrganizations,
      ...search,
    });
  }, [currentPage, currentSize, getVacations, search, sortInfo.value, sortOption, allAvailableOrganizations]);

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };

  const columns: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: _ => ({
        onClick: () => sortTrigger(),
      }),
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Вид отпуска',
      dataIndex: 'vacationType',
      key: 'vacationType',
    },
    {
      title: 'Шаг согласования',
      dataIndex: 'applyStep',
      key: 'applyStep',
    },
    {
      title: 'Тип заявления',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Дата начала',
      dataIndex: 'startedAt',
      key: 'startedAt',
      sorter: (a, b) => sortingDate(a.startedAt, b.startedAt),
    },
    {
      title: 'Дата окончания',
      dataIndex: 'endedAt',
      key: 'endedAt',
      sorter: (a, b) => sortingDate(a.endedAt, b.endedAt),
    },
    {
      title: 'Дни',
      dataIndex: 'amountDays',
      key: 'amountDays',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (
        status: {
          type?: PresetStatusColorType;
          text: string;
          isSupervisorApproved: boolean;
          steps: number | null;
        },
        render,
      ) => (
        <>
          <span className="d-flex">
            <Badge className={style.badge} status={DayOffsStatusTypesMap[status.type || 'processing']} />
            {status.text}
          </span>
          <span className="d-flex">
            <Badge status={(render as unknown as TVacationInfo).zupRequestStatus?.type} />
            {(render as unknown as TVacationInfo).zupRequestStatus?.type === 'success' ? 'Выгружено в 1С' : null}
            {(render as unknown as TVacationInfo).zupRequestStatus?.type === 'processing' ? 'Отправлено в 1С' : null}
            {(render as unknown as TVacationInfo).zupRequestStatus?.type === 'error' ? 'Ошибка выгрузки' : null}
          </span>
          {status.steps && !status.isSupervisorApproved && (
            <>
              <br />
              <SuperviserApproveMarker text="Не согласовано руководителем" />
            </>
          )}
        </>
      ),
    },
  ];

  useEffect(() => {
    if (data) {
      const dataTable = dataForVacationTable(data?.data);
      setDataVacations(dataTable);
    }
  }, [data]);

  return (
    <>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <>
          {dataVacations && columns && (
            <TableArchive
              dataSource={dataVacations}
              columns={columns}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              currentSize={currentSize}
              setCurrentSize={setCurrentSize}
              total={data?.pagination?.total}
              tabKey="otherArchive"
              onSearch={onSearch}
            />
          )}
        </>
      )}
    </>
  );
};
